import { createSlice } from '@reduxjs/toolkit';
import { getClinicFeaturesApi } from '../../../rtk-apis/clinics/getClinicFeatures';
import {
  DEFAULT_CLINIC_FEATURES,
  getClinicFeatures,
  type ClinicFeatures,
} from '../../../Utils/clinicFeatures';

/**
 * The logged-in clinic's feature flags.
 *
 * `features` is seeded from the storage cache rather than from the hardcoded
 * defaults, so a reload shows the clinic's real configuration on first paint
 * instead of flashing a viewer button that is about to disappear.
 */
interface State {
  status: string;
  features: ClinicFeatures;
  /** True once a response (success or failure) has been seen this session. */
  fetched: boolean;
  loading: boolean;
  msg: string;
}

const initialState: State = {
  status: '',
  features: getClinicFeatures(),
  fetched: false,
  loading: false,
  msg: '',
};

const getClinicFeaturesSlice = createSlice({
  name: 'getClinicFeatures',
  initialState,
  reducers: {
    clearState: state => {
      state.status = '';
      state.features = { ...DEFAULT_CLINIC_FEATURES };
      state.fetched = false;
      state.loading = false;
      state.msg = '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getClinicFeaturesApi.pending, state => {
        state.loading = true;
      })
      .addCase(getClinicFeaturesApi.fulfilled, (state, action) => {
        state.loading = false;
        state.status = 'SUCCESS';
        state.fetched = true;
        const incoming = (action?.payload as any)?.result?.features;
        if (incoming) {
          state.features = {
            dicomViewer:
              typeof incoming.dicomViewer === 'boolean'
                ? incoming.dicomViewer
                : DEFAULT_CLINIC_FEATURES.dicomViewer,
            measurement:
              typeof incoming.measurement === 'boolean'
                ? incoming.measurement
                : DEFAULT_CLINIC_FEATURES.measurement,
          };
        }
        state.msg = (action?.payload as any)?.msg || '';
      })
      .addCase(getClinicFeaturesApi.rejected, (state, action) => {
        // Keep whatever was cached. A failed fetch must not silently withdraw a
        // feature the clinic has — the server still enforces the real answer.
        state.loading = false;
        state.status = 'ERROR';
        state.fetched = true;
        state.msg = (action?.payload as string) || 'Something went wrong!';
      });
  },
});

export default getClinicFeaturesSlice.reducer;
export const { clearState } = getClinicFeaturesSlice.actions;
